import React, { useEffect, useCallback, useState, useRef } from 'react';
import { Form, Formik } from 'formik';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { TRANSPORT } from 'helpers/routes';
import { SET_MAIN_PAGE_SEARCH } from 'store/mainPageSearch';
import { TransportService } from 'service/TransportService';
import ExchangeTransportSearch from 'models/exchangeTransportSearch';
import { DEFAULT_ADDRESS_RADIUS } from 'constants/settings';
import { socket } from 'helpers/socket';

import SearchExchange from 'components/TransportComponents/SearchExchange';
import AllTransportTable from 'components/TransportComponents/TransportTable';
import TransportSearchTabs from 'components/TransportComponents/TransportSearchTabs';
import TransportQuickSearchBar from 'components/TransportComponents/TransportQuickSearchBar';
import {
  MobileStatusFilters,
  RefreshButton,
  SearchFormButtons,
  searchPageStyles as styles,
} from 'components/common/SearchPage';
import { useSearchQueryParams } from '@/hooks/useSearchQueryParams';
import { useNewItemsNotification } from '@/hooks/useNewItemsNotification';
import {
  urlStatusToInternal,
  internalStatusToUrl,
  getStatusesForFilter,
} from '@/utils/searchStatusUtils';

import { searchValidateSchema } from './validate';

const ITEMS_PER_LOAD = 10;

const TransportSearch = () => {
  const searchFromStore = useSelector((state) => state.mainPageSearch);
  const authenticated = useSelector((state) => state.auth.authenticated);
  const lang = useSelector((state) => state.settings.lang);

  const prevTransportIdRef = useRef(null);
  const initialLoadDoneRef = useRef(false);
  const pendingTemplateResolveRef = useRef(null);
  const sentinelRef = useRef(null);
  const tableContainerRef = useRef(null);
  const chunkCountRef = useRef(0);
  const initialValues = useRef({
    ...ExchangeTransportSearch.initialValues(),
    ...searchFromStore,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [data, setData] = useState({ items: [], count: 0, nextCursor: undefined });
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAdditionalOpen, setIsAdditionalOpen] = useState(false);
  const [activeTemplateId, setActiveTemplateId] = useState(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState(null);
  const [isCreatingNewTemplate, setIsCreatingNewTemplate] = useState(false);
  const [searchTabsKey, setSearchTabsKey] = useState(0);
  const [templates, setTemplates] = useState([]);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {
    entityId: transportId,
    templateId: urlTemplateId,
    status: urlStatus,
    setEntityId: setTransportId,
    updateQueryParams,
  } = useSearchQueryParams(TRANSPORT.search, 'transportId');

  const {
    newItemsCounts,
    showRefreshButton,
    setShowRefreshButton,
    clearNewItemsCount,
  } = useNewItemsNotification(authenticated, activeTemplateId, templates);

  const handleToggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  const handleToggleAdditional = useCallback(() => {
    setIsAdditionalOpen((prev) => !prev);
  }, []);

  const fetchTemplate = useCallback(async () => {
    if (location.state && location.state.templateId) {
      await TransportService.getExchangePattern(location.state.templateId, lang).then((result) => {
        const templateData = result.data;

        // Convert template data to form values
        initialValues.current = {
          ...ExchangeTransportSearch.initialValues(),
          ...ExchangeTransportSearch.formData(templateData),
        };
      });
    }
  }, [lang, location.state]);

  const fetchData = useCallback(
    async (cursor, templateData, statusFilter, append = false) => {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
        setData({ items: [], count: 0, nextCursor: undefined });
        setHasMore(true);
        chunkCountRef.current = 0;
      }

      const currentStatusFilter = statusFilter !== undefined ? statusFilter : activeStatusFilter;

      const searchParams = {
        lang,
        limit: ITEMS_PER_LOAD,
      };

      const statuses = getStatusesForFilter(currentStatusFilter);

      // If status filter is active, don't apply search/template filters - only status
      let requestData = {};
      if (statuses) {
        requestData.statuses = statuses;
        if (cursor) {
          requestData.cursor = cursor;
        }
      } else {
        // Only apply search filters when no status filter is active
        const searchData = templateData
          ? ExchangeTransportSearch.searchData(templateData)
          : ExchangeTransportSearch.searchData(initialValues.current);
        requestData = { ...searchData, cursor };
      }

      TransportService.searchExchange(searchParams, requestData)
        .then((result) => {
          if (append) {
            setData((prev) => ({
              items: [...prev.items, ...result.data.items],
              count: result.data.count,
              nextCursor: result.data.nextCursor,
            }));
          } else {
            setData(result.data);
          }
          setHasMore(!!result.data.nextCursor);
          chunkCountRef.current += 1;
        })
        .finally(() => {
          setIsLoading(false);
          setIsLoadingMore(false);
        });
    },
    [lang, activeStatusFilter],
  );

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore && !isLoading && data.nextCursor) {
      fetchData(data.nextCursor, undefined, activeStatusFilter, true);
    }
  }, [hasMore, isLoadingMore, isLoading, data.nextCursor, fetchData, activeStatusFilter]);

  // Store loadMore in a ref so the observer doesn't need to be recreated on every state change
  const loadMoreRef = useRef(loadMore);
  loadMoreRef.current = loadMore;
  const observerRef = useRef(null);

  // Setup IntersectionObserver once
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const container = tableContainerRef.current;

    if (!sentinel || !container) return;

    // Use container as root on desktop (scrollable), viewport on mobile (page scrolls)
    const root = getComputedStyle(container).overflow === 'visible' ? null : container;

    observerRef.current = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          loadMoreRef.current();
        }
      },
      {
        root,
        rootMargin: '500px',
        threshold: 0,
      }
    );

    observerRef.current.observe(sentinel);
    return () => observerRef.current?.disconnect();
  }, []);

  // Auto-fetch 2nd chunk after initial load
  useEffect(() => {
    if (isLoading || isLoadingMore) return;
    if (!data.nextCursor) return;

    if (chunkCountRef.current === 1) {
      chunkCountRef.current = 2;
      loadMoreRef.current();
    }
  }, [isLoading, isLoadingMore, data.nextCursor]);

  const handleSelectTemplate = useCallback(
    async (templateId) => {
      // Cancel new template creation when switching to another template
      if (isCreatingNewTemplate) {
        setIsCreatingNewTemplate(false);
      }

      setActiveTemplateId(templateId);
      setShowRefreshButton(false);
      // Clear status filter when selecting a search tab/template
      setActiveStatusFilter(null);
      // Update URL: clear status and set templateId in one call
      updateQueryParams({ status: null, templateId });

      // Clear new items count when selecting template
      if (templateId) {
        clearNewItemsCount(templateId);
      }

      if (templateId === null) {
        initialValues.current = ExchangeTransportSearch.initialValues();
        setIsExpanded(false);
        // Pass null explicitly to override stale activeStatusFilter from closure
        fetchData(undefined, undefined, null);
      } else {
        try {
          const response = await TransportService.getExchangePattern(templateId, lang);
          const fullTemplateData = response.data.data;

          // Convert template data to form values
          const formData = {
            ...ExchangeTransportSearch.initialValues(),
            ...ExchangeTransportSearch.formData(fullTemplateData),
          };
          initialValues.current = formData;

          // Pass converted form data to fetchData for search (includes additional addresses and exclusions)
          // Pass null explicitly to override stale activeStatusFilter from closure
          fetchData(undefined, formData, null);
        } catch (error) {
          console.error('Failed to fetch template data:', error);
        }
      }
    },
    [fetchData, lang, isCreatingNewTemplate, updateQueryParams],
  );

  const handleAddTemplate = useCallback(() => {
    setIsCreatingNewTemplate(true);
    setIsExpanded(true);
    setActiveStatusFilter(null);
    updateQueryParams({ status: null });
    initialValues.current = ExchangeTransportSearch.initialValues();
  }, [updateQueryParams]);

  const handleCancelNewTemplate = useCallback(() => {
    setIsCreatingNewTemplate(false);
    setIsExpanded(false);
    initialValues.current = ExchangeTransportSearch.initialValues();
  }, []);

  const handleStatusFilterChange = useCallback((status) => {
    setActiveStatusFilter(status);
    setActiveTemplateId(null);
    setIsExpanded(false);
    setIsCreatingNewTemplate(false);
    // Update URL with status and clear templateId in one call to avoid race condition
    updateQueryParams({ status: internalStatusToUrl(status), templateId: null });
    // Fetch data with the new status filter
    fetchData(undefined, undefined, status);
  }, [fetchData, updateQueryParams]);

  const handleRefresh = useCallback(() => {
    setShowRefreshButton(false);
    fetchData();
  }, [fetchData]);

  const loadData = useCallback(async () => {
    // If there's a templateId in URL, use handleSelectTemplate instead of fetchTemplate
    // This handles page refresh when location.state is lost
    if (urlTemplateId) {
      // If template is already loaded, skip (infinite scroll handles subsequent loads)
      if (urlTemplateId === activeTemplateId) {
        return;
      }
      handleSelectTemplate(urlTemplateId);
      return;
    }

    await fetchTemplate();
    fetchData();
  }, [fetchData, fetchTemplate, urlTemplateId, handleSelectTemplate, activeTemplateId]);

  const updateData = useCallback(() => {
    fetchData();
  }, [fetchData]);

  const handleTransportSelect = useCallback(
    (selectedTransportId) => {
      setTransportId(selectedTransportId);
      setData((prevState) => ({
        ...prevState,
        items: prevState.items.map((item) => ({
          ...item,
          is_seen: item.is_seen || selectedTransportId === item.id,
        })),
      }));
    },
    [setTransportId],
  );

  useEffect(() => {
    return () => {
      dispatch({
        type: SET_MAIN_PAGE_SEARCH,
        payload: {
          loading_address: null,
          loading_address_radius: DEFAULT_ADDRESS_RADIUS,
          unloading_address: null,
          unloading_address_radius: DEFAULT_ADDRESS_RADIUS,
        },
      });
    };
  }, [dispatch]);

  // Handle templateId from URL (when coming from map or direct link)
  useEffect(() => {
    if (urlTemplateId && urlTemplateId !== activeTemplateId) {
      handleSelectTemplate(urlTemplateId);
    }
  }, [urlTemplateId]);

  // Handle status from URL (when coming from direct link)
  useEffect(() => {
    const internalStatus = urlStatusToInternal(urlStatus);
    if (internalStatus && internalStatus !== activeStatusFilter) {
      setActiveStatusFilter(internalStatus);
      setActiveTemplateId(null);
      setIsExpanded(false);
      fetchData(undefined, undefined, internalStatus);
    }
  }, [urlStatus]);

  // Listen for template created event to get new template ID
  useEffect(() => {
    if (!authenticated) return;

    const handleTemplateCreated = (data) => {
      const newTemplate = Array.isArray(data) ? data[0] : data;
      if (newTemplate?.id && pendingTemplateResolveRef.current) {
        pendingTemplateResolveRef.current(newTemplate.id);
        pendingTemplateResolveRef.current = null;
      }
    };

    const setupListener = () => {
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.on('search_templates.created', handleTemplateCreated);
        return true;
      }
      return false;
    };

    let intervalId = null;
    if (!setupListener()) {
      intervalId = setInterval(setupListener, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
      const socketInstance = socket.instance;
      if (socketInstance) {
        socketInstance.off('search_templates.created', handleTemplateCreated);
      }
    };
  }, [authenticated]);

  const handleSubmit = useCallback(
    async (values, { setSubmitting }) => {
      setSubmitting(false);

      const templateData = ExchangeTransportSearch.templateData(values);

      if (isCreatingNewTemplate) {
        try {
          // Create promise to wait for socket event with template ID
          const waitForTemplateId = new Promise((resolve) => {
            pendingTemplateResolveRef.current = resolve;
            // Timeout after 5 seconds
            setTimeout(() => {
              if (pendingTemplateResolveRef.current === resolve) {
                pendingTemplateResolveRef.current = null;
                resolve(null);
              }
            }, 5000);
          });

          // Save template - API might not return ID
          const response = await TransportService.saveExchangePattern(templateData);
          let newTemplateId = response.data?.data?.id || response.data?.id || response.data?.[0]?.id;

          // If no ID from response, wait for socket event
          if (!newTemplateId) {
            newTemplateId = await waitForTemplateId;
          }

          // Always refresh tabs list
          setIsCreatingNewTemplate(false);
          setSearchTabsKey((prev) => prev + 1);

          if (newTemplateId) {
            // Set the new template as active
            setActiveTemplateId(newTemplateId);

            const fullTemplateResponse = await TransportService.getExchangePattern(newTemplateId, lang);
            const fullTemplateData = fullTemplateResponse.data;
            // Convert template data to form values
            initialValues.current = {
              ...ExchangeTransportSearch.initialValues(),
              ...ExchangeTransportSearch.formData(fullTemplateData),
            };
            fetchData(undefined, fullTemplateData);
          } else {
            // No ID returned - just search with current values
            initialValues.current = { ...values };
            fetchData(undefined, values);
          }
        } catch (error) {
          console.error('Failed to save template:', error);
        }
        return;
      }

      if (activeTemplateId) {
        try {
          await TransportService.updateExchangePattern(activeTemplateId, templateData);
          setSearchTabsKey((prev) => prev + 1);
        } catch (error) {
          console.error('Failed to update template:', error);
        }
      }

      initialValues.current = { ...values };

      if (location.pathname.includes(TRANSPORT.search)) {
        fetchData();
      } else {
        navigate(TRANSPORT.search);
      }

      setIsAdditionalOpen(false);
      setIsExpanded(false);
    },
    [navigate, fetchData, location.pathname, isCreatingNewTemplate, activeTemplateId, lang, updateQueryParams],
  );

  useEffect(() => {
    const wasCardJustClosed = prevTransportIdRef.current !== null && transportId === null;
    const isInitialLoad = !initialLoadDoneRef.current;

    // Update prev transportId ref
    prevTransportIdRef.current = transportId;

    // Don't refresh if we just closed a card
    if (wasCardJustClosed) {
      return;
    }

    // Don't refresh if card is open (unless it's initial load)
    if (transportId && !isInitialLoad) {
      return;
    }

    // Skip initial load if URL has status filter - let the status effect handle it
    if (isInitialLoad && urlStatus) {
      initialLoadDoneRef.current = true;
      return;
    }

    // Initial load
    if (isInitialLoad) {
      initialLoadDoneRef.current = true;
      loadData();
    }
  }, [loadData, transportId, urlStatus]);

  return (
    <Formik enableReinitialize initialValues={initialValues.current} onSubmit={handleSubmit} validationSchema={searchValidateSchema(t)}>
      {({ resetForm }) => (
        <Form>
          <div className={styles.mainContainer}>
            {/* Search Template Tabs */}
            <TransportSearchTabs
              activeTemplateId={activeTemplateId}
              onSelectTemplate={handleSelectTemplate}
              onAddTemplate={handleAddTemplate}
              newItemsCounts={newItemsCounts}
              isCreatingNew={isCreatingNewTemplate}
              onCancelNewTemplate={handleCancelNewTemplate}
              refreshTrigger={searchTabsKey}
              activeStatusFilter={activeStatusFilter}
              onStatusFilterChange={handleStatusFilterChange}
              onTemplatesLoaded={setTemplates}
            />

            {/* Quick Search Bar */}
            <TransportQuickSearchBar
              isExpanded={isExpanded}
              onToggleExpanded={handleToggleExpanded}
              authenticated={authenticated}
              hideExpandButton={!!activeStatusFilter}
            />
            {/* Expanded Search Form */}
            {isExpanded && (
              <div className={styles.expandedSearchContainer}>
                <SearchExchange isOpen={isAdditionalOpen} />
                <SearchFormButtons
                  isAdditionalOpen={isAdditionalOpen}
                  onToggleAdditional={handleToggleAdditional}
                  onClearForm={() => {
                    resetForm();
                    navigate(TRANSPORT.search);
                    initialValues.current = ExchangeTransportSearch.initialValues();
                  }}
                />
              </div>
            )}

            {/* Refresh Button - shown when active template has new items */}
            {showRefreshButton && (
              <RefreshButton onClick={handleRefresh} translationKey="transportSearch.refresh" />
            )}

            {/* Mobile Status Filters - shown above table on mobile */}
            {authenticated && (
              <MobileStatusFilters
                activeStatusFilter={activeStatusFilter}
                onStatusFilterChange={handleStatusFilterChange}
                translationPrefix="transportSearch"
              />
            )}

            {/* Results Table */}
            <div className={styles.tableContainer} ref={tableContainerRef}>
              <AllTransportTable
                data={data}
                urlToAdd={TRANSPORT.addToExchange}
                updateData={updateData}
                urlToPage={TRANSPORT.search}
                selectedId={transportId}
                onSelect={handleTransportSelect}
                showStatusColumn={!!activeStatusFilter}
                isLoading={isLoading}
                showAddButton={!activeStatusFilter}
              />
              {/* Sentinel for IntersectionObserver */}
              <div ref={sentinelRef} className={styles.sentinel}>
                {isLoadingMore && (
                  <div className={styles.loadingIndicator}>
                    {t('common.loading', 'Загрузка...')}
                  </div>
                )}
              </div>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default TransportSearch;
